<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import SelectKnowledgeBaseDialog from 'dashboard/components-next/pilot/pageComponents/knowledgeBase/SelectKnowledgeBaseDialog.vue';

const props = defineProps({
  kbRule: {
    type: Object,
    default: null,
  },
  // 知识库是否锁定（锁定后不能更换）
  isKbLocked: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save', 'clear']);

const store = useStore();
const { t } = useI18n();

const showDrawer = ref(false);
const isSaving = ref(false);
const isClearing = ref(false);

// 本地编辑状态
const localRule = ref({
  enabled: false,
  mode: 'keyword', // 'keyword' | 'open'
  keywords: [],
  matchMode: 'any',
});

// 本地知识库信息
const localKbInfo = ref({
  knowledgeBaseId: null,
  knowledgeBaseName: null,
  logoUrl: null,
  description: null,
});

// 假数据（模拟关键词）
const mockKeywords = ['企业直播', '微信营销', 'B2B直播'];

// 知识库选择弹窗引用
const selectKbDialogRef = ref(null);

// 关键词编辑状态
const newKeyword = ref('');
const editingIndex = ref(null);
const editingContent = ref('');

// 显示的关键词列表（带 id）
const displayKeywords = computed(() =>
  localRule.value.keywords.map((content, idx) => ({ id: idx, content }))
);

// 打开抽屉
const openDrawer = () => {
  showDrawer.value = true;
};

// 关闭抽屉
const closeDrawer = () => {
  showDrawer.value = false;
  emit('close');
};

// 初始化本地状态
const initLocalState = () => {
  if (props.kbRule) {
    localRule.value = {
      id: props.kbRule.rule?.id,
      enabled: props.kbRule.rule?.enabled === true,
      mode: props.kbRule.rule?.mode || 'keyword',
      keywords: [...(props.kbRule.rule?.keywords || [])],
      matchMode: props.kbRule.rule?.matchMode || 'any',
    };
    // 初始化知识库信息
    localKbInfo.value = {
      knowledgeBaseId: props.kbRule.knowledgeBaseId,
      knowledgeBaseName: props.kbRule.knowledgeBaseName,
      logoUrl: props.kbRule.logoUrl,
      description: props.kbRule.description,
    };
  }
};

// 打开知识库选择弹窗
const openSelectKbDialog = () => {
  selectKbDialogRef.value?.open();
};

// 处理知识库选择
const handleKbSelect = async kbId => {
  try {
    const kbResponse = await store.dispatch('pilotKnowledgeBases/show', {
      id: kbId,
    });
    const kb = kbResponse.payload;

    localKbInfo.value = {
      knowledgeBaseId: kb.id,
      knowledgeBaseName: kb.name,
      logoUrl: kb.logo_url,
      description: kb.description,
    };
  } catch (error) {
    console.error('Failed to get knowledge base:', error);
    useAlert(
      t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.ADD_KB_ERROR')
    );
  }
};

// 添加关键词
const addKeyword = content => {
  const keyword = content.trim();
  if (keyword && !localRule.value.keywords.includes(keyword)) {
    localRule.value.keywords.push(keyword);
  }
};

// 开始编辑关键词
const startEditKeyword = index => {
  editingIndex.value = index;
  editingContent.value = localRule.value.keywords[index];
};

// 保存编辑关键词
const saveEditKeyword = () => {
  if (editingIndex.value !== null && editingContent.value.trim()) {
    localRule.value.keywords[editingIndex.value] = editingContent.value.trim();
  }
  editingIndex.value = null;
  editingContent.value = '';
};

// 取消编辑
const cancelEdit = () => {
  editingIndex.value = null;
  editingContent.value = '';
};

// 删除关键词
const removeKeyword = index => {
  localRule.value.keywords.splice(index, 1);
};

// 保存配置
const saveConfig = async () => {
  // 检查是否选择了知识库
  if (!localKbInfo.value.knowledgeBaseId) {
    useAlert(
      t(
        'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.SELECT_KB_FIRST'
      )
    );
    return;
  }

  // 关键词模式下验证关键词不能为空
  if (
    localRule.value.mode === 'keyword' &&
    localRule.value.keywords.length === 0
  ) {
    useAlert(
      t(
        'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.KEYWORDS_REQUIRED'
      )
    );
    return;
  }

  isSaving.value = true;
  try {
    const config = {
      mode: localRule.value.mode,
      keywords:
        localRule.value.mode === 'keyword' ? localRule.value.keywords : [],
      match_mode:
        localRule.value.mode === 'keyword' ? localRule.value.matchMode : 'any',
    };

    // 根据规则是否存在，决定调用 create 还是 update
    if (localRule.value.id) {
      await store.dispatch('pilotWechatCrawl/update', {
        knowledge_base_id: localKbInfo.value.knowledgeBaseId,
        wechat_crawl: { enabled: localRule.value.enabled, config },
      });
    } else {
      const response = await store.dispatch('pilotWechatCrawl/create', {
        knowledge_base_id: localKbInfo.value.knowledgeBaseId,
        wechat_crawl: { enabled: localRule.value.enabled, config },
      });
      // 更新本地 id
      if (response?.rule?.id) {
        localRule.value.id = response.rule.id;
      }
    }

    useAlert(t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.SAVE_SUCCESS'));

    // 通知父组件
    emit('save', {
      knowledgeBaseId: localKbInfo.value.knowledgeBaseId,
      knowledgeBaseName: localKbInfo.value.knowledgeBaseName,
      logoUrl: localKbInfo.value.logoUrl,
      description: localKbInfo.value.description,
      rule: { ...localRule.value },
    });

    closeDrawer();
  } catch (error) {
    console.error('Failed to save config:', error);
    useAlert(t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.SAVE_FAILED'));
  } finally {
    isSaving.value = false;
  }
};

// 清空规则
const clearRule = async () => {
  if (!localKbInfo.value.knowledgeBaseId) return;

  isClearing.value = true;
  try {
    await store.dispatch(
      'pilotWechatCrawl/destroy',
      localKbInfo.value.knowledgeBaseId
    );

    useAlert(t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.CLEAR_SUCCESS'));

    // 通知父组件清空规则
    emit('clear', {
      knowledgeBaseId: localKbInfo.value.knowledgeBaseId,
    });

    closeDrawer();
  } catch (error) {
    console.error('Failed to clear rule:', error);
    useAlert(t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.CLEAR_FAILED'));
  } finally {
    isClearing.value = false;
  }
};

// 是否有规则
const hasRule = computed(() => !!localRule.value.id);

// 暴露方法供父组件调用
defineExpose({
  open: openDrawer,
  close: closeDrawer,
});

// 监听 kbRule 变化
watch(
  () => props.kbRule,
  newVal => {
    if (newVal) {
      initLocalState();
      openDrawer();
    }
  },
  { immediate: true }
);
</script>

<template>
  <Teleport to="body">
    <!-- 遮罩层 -->
    <transition
      enter-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showDrawer && kbRule"
        class="fixed inset-0 bg-black/50 z-[9998]"
        @click="closeDrawer"
      />
    </transition>

    <!-- 抽屉面板 -->
    <transition
      enter-active-class="transition-transform duration-300 ease-out"
      enter-from-class="-translate-x-full"
      enter-to-class="translate-x-0"
      leave-active-class="transition-transform duration-200 ease-in"
      leave-from-class="translate-x-0"
      leave-to-class="-translate-x-full"
    >
      <div
        v-if="showDrawer && kbRule"
        class="fixed left-0 top-0 h-full w-[31.25rem] bg-n-solid-1 dark:bg-n-slate-900 shadow-2xl z-[9999] flex flex-col"
      >
        <!-- 头部 -->
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-slate-6 dark:border-n-slate-700"
        >
          <h3 class="text-lg font-medium text-n-slate-12">
            {{
              t(
                'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.DRAWER_TITLE'
              )
            }}
          </h3>
          <button
            class="p-1.5 size-8 rounded-md hover:bg-n-slate-2 dark:hover:bg-n-slate-800 transition-colors flex items-center justify-center"
            @click="closeDrawer"
          >
            <Icon icon="i-lucide-x" class="size-5 text-n-slate-11" />
          </button>
        </div>

        <!-- 内容区域 -->
        <div class="flex-1 overflow-y-auto px-6 py-4">
          <div class="flex flex-col gap-6">
            <!-- 知识库信息（可点击选择） -->
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                {{
                  t(
                    'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.KNOWLEDGE_BASE'
                  )
                }}
              </label>
              <!-- 已选择知识库 -->
              <button
                v-if="localKbInfo.knowledgeBaseId && !isKbLocked"
                class="w-full p-3 rounded-lg bg-n-slate-2 dark:bg-n-slate-800 hover:bg-n-slate-3 dark:hover:bg-n-slate-700 transition-colors text-left"
                @click="openSelectKbDialog"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="w-10 h-10 rounded-lg bg-n-slate-3 dark:bg-n-slate-7 flex items-center justify-center flex-shrink-0 overflow-hidden"
                  >
                    <img
                      v-if="localKbInfo.logoUrl"
                      :src="localKbInfo.logoUrl"
                      :alt="localKbInfo.knowledgeBaseName"
                      class="w-full h-full object-cover"
                    />
                    <span v-else class="text-xl font-semibold text-n-slate-7">
                      {{ localKbInfo.knowledgeBaseName?.charAt(0) || '?' }}
                    </span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <span
                      class="text-sm font-medium text-n-slate-12 truncate block"
                    >
                      {{ localKbInfo.knowledgeBaseName }}
                    </span>
                    <span class="text-xs text-n-slate-11 truncate block">
                      {{
                        localKbInfo.description ||
                        t(
                          'PILOT.KNOWLEDGE_BASES.SELECT_KB_DIALOG.NO_DESCRIPTION'
                        )
                      }}
                    </span>
                  </div>
                  <Icon
                    icon="i-lucide-chevron-right"
                    class="text-n-slate-10 size-5 flex-shrink-0"
                  />
                </div>
              </button>
              <!-- 已选择知识库（锁定状态，不可更换） -->
              <div
                v-else-if="localKbInfo.knowledgeBaseId && isKbLocked"
                class="w-full p-3 rounded-lg bg-n-slate-2 dark:bg-n-slate-800 text-left"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="w-10 h-10 rounded-lg bg-n-slate-3 dark:bg-n-slate-7 flex items-center justify-center flex-shrink-0 overflow-hidden"
                  >
                    <img
                      v-if="localKbInfo.logoUrl"
                      :src="localKbInfo.logoUrl"
                      :alt="localKbInfo.knowledgeBaseName"
                      class="w-full h-full object-cover"
                    />
                    <span v-else class="text-xl font-semibold text-n-slate-7">
                      {{ localKbInfo.knowledgeBaseName?.charAt(0) || '?' }}
                    </span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <span
                      class="text-sm font-medium text-n-slate-12 truncate block"
                    >
                      {{ localKbInfo.knowledgeBaseName }}
                    </span>
                    <span class="text-xs text-n-slate-11 truncate block">
                      {{
                        localKbInfo.description ||
                        t(
                          'PILOT.KNOWLEDGE_BASES.SELECT_KB_DIALOG.NO_DESCRIPTION'
                        )
                      }}
                    </span>
                  </div>
                </div>
              </div>
              <!-- 未选择知识库 -->
              <button
                v-else
                class="w-full p-4 rounded-lg border-2 border-dashed border-n-slate-3 dark:border-n-slate-6 hover:border-n-brand dark:hover:border-n-brand transition-colors"
                @click="openSelectKbDialog"
              >
                <div class="flex flex-col items-center justify-center gap-2">
                  <div
                    class="w-10 h-10 rounded-lg bg-n-slate-2 dark:bg-n-slate-7 flex items-center justify-center"
                  >
                    <Icon icon="i-lucide-plus" class="text-n-slate-10 size-5" />
                  </div>
                  <span class="text-sm text-n-slate-11">
                    {{
                      t(
                        'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.SELECT_KB_BUTTON'
                      )
                    }}
                  </span>
                </div>
              </button>
            </div>

            <!-- 标题区域：过滤抓取的公众号图文 + 开关 -->
            <div class="p-4 bg-n-slate-2 dark:bg-n-slate-800 rounded-2xl">
              <div class="flex items-center justify-between">
                <div>
                  <h4 class="text-sm font-medium text-n-slate-12">
                    {{
                      t(
                        'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.FILTER_TITLE'
                      )
                    }}
                  </h4>
                  <p class="text-xs text-n-slate-11 mt-1">
                    {{
                      t(
                        'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.FILTER_HINT'
                      )
                    }}
                  </p>
                </div>
                <div class="flex items-center gap-2">
                  <Switch
                    :model-value="localRule.enabled"
                    :label="
                      t(
                        'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.FILTER_ENABLED_LABEL'
                      )
                    "
                    @change="localRule.enabled = $event"
                  />
                </div>
              </div>
            </div>

            <!-- 接收模式区域 -->
            <div class="p-4 bg-n-slate-2 dark:bg-n-slate-800 rounded-2xl">
              <label class="block text-sm font-medium text-n-slate-11 mb-3">
                {{
                  t(
                    'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.MODE_LABEL'
                  )
                }}
              </label>
              <div class="flex flex-col gap-3">
                <!-- 开放模式（禁用状态） -->
                <div
                  class="flex items-start gap-3 p-3 rounded-lg bg-n-slate-1 dark:bg-n-slate-7 opacity-50 cursor-not-allowed"
                >
                  <input type="radio" disabled class="mt-0.5" />
                  <div class="flex-1">
                    <span class="text-sm font-medium text-n-slate-12 block">
                      {{
                        t(
                          'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.MODE_OPEN_LABEL'
                        )
                      }}
                    </span>
                    <span class="text-xs text-n-slate-11 mt-0.5 block">
                      {{
                        t(
                          'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.MODE_OPEN_DESC'
                        )
                      }}
                    </span>
                  </div>
                </div>
                <!-- 关键词过滤模式 -->
                <label
                  class="flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors"
                  :class="[
                    localRule.mode === 'keyword'
                      ? 'bg-n-brand/10 ring-1 ring-n-brand'
                      : 'bg-n-slate-1 dark:bg-n-slate-7 hover:bg-n-slate-2 dark:hover:bg-n-slate-6',
                  ]"
                >
                  <input
                    v-model="localRule.mode"
                    type="radio"
                    value="keyword"
                    class="mt-0.5"
                  />
                  <div class="flex-1">
                    <span class="text-sm font-medium text-n-slate-12 block">
                      {{
                        t(
                          'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.MODE_KEYWORD_LABEL'
                        )
                      }}
                    </span>
                    <span class="text-xs text-n-slate-11 mt-0.5 block">
                      {{
                        t(
                          'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.MODE_KEYWORD_DESC'
                        )
                      }}
                    </span>
                  </div>
                </label>
              </div>
            </div>

            <!-- 关键词配置区域（仅关键词模式显示） -->
            <div
              v-if="localRule.mode === 'keyword'"
              class="gradient-border-wrapper rounded-2xl"
            >
              <div class="bg-n-slate-2 dark:bg-n-slate-800 rounded-2xl">
                <!-- 关键词列表 -->
                <div class="p-4 space-y-2">
                  <!-- 空状态：假数据 + 渐变蒙层 + 标题描述 -->
                  <div
                    v-if="localRule.keywords.length === 0"
                    class="relative overflow-hidden rounded-xl"
                  >
                    <!-- 假数据关键词列表（半透明） -->
                    <div
                      class="w-full space-y-2 opacity-50 pointer-events-none"
                    >
                      <div
                        v-for="(mockKeyword, index) in mockKeywords"
                        :key="index"
                        class="flex py-3 px-4 items-center gap-3 rounded-xl bg-n-solid-2 outline-1 outline outline-n-container"
                      >
                        <span
                          class="flex items-center gap-2 text-sm text-n-slate-12 flex-1"
                        >
                          {{ mockKeyword }}
                        </span>
                      </div>
                    </div>

                    <!-- 渐变蒙层 + 标题/描述 -->
                    <div
                      class="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-transparent from-0% via-white/50 dark:via-n-slate-800/50 to-white dark:to-n-slate-800"
                    >
                      <div
                        class="flex flex-col items-center justify-center gap-2"
                      >
                        <h5
                          class="text-lg font-medium text-center text-n-slate-12"
                        >
                          {{
                            t(
                              'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.EMPTY_TITLE'
                            )
                          }}
                        </h5>
                        <p class="max-w-sm text-sm text-center text-n-slate-11">
                          {{
                            t(
                              'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.EMPTY_DESCRIPTION'
                            )
                          }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- 有关键词时显示列表 -->
                  <div v-else class="flex flex-col gap-2">
                    <div
                      v-for="keyword in displayKeywords"
                      :key="keyword.id"
                      class="flex py-3 px-4 items-center gap-3 rounded-xl bg-n-solid-2 outline-1 outline outline-n-container"
                      :class="{
                        'ring-2 ring-n-brand': editingIndex === keyword.id,
                      }"
                    >
                      <!-- 编辑模式 -->
                      <InlineInput
                        v-if="editingIndex === keyword.id"
                        v-model="editingContent"
                        focus-on-mount
                        class="flex-1"
                        @keyup.enter="saveEditKeyword"
                        @blur="saveEditKeyword"
                        @keyup.escape="cancelEdit"
                        @click.stop
                      />
                      <!-- 显示模式 -->
                      <span
                        v-else
                        class="flex items-center gap-2 text-sm text-n-slate-12 flex-1"
                      >
                        {{ keyword.content }}
                      </span>
                      <!-- 操作按钮 -->
                      <div class="flex items-center gap-1" @click.stop>
                        <Button
                          v-if="editingIndex !== keyword.id"
                          icon="i-lucide-pen"
                          slate
                          xs
                          ghost
                          @click="startEditKeyword(keyword.id)"
                        />
                        <span class="w-px h-4 bg-n-weak" />
                        <Button
                          icon="i-lucide-trash"
                          slate
                          xs
                          ghost
                          @click="removeKeyword(keyword.id)"
                        />
                      </div>
                    </div>
                  </div>

                  <!-- 添加新关键词输入框 -->
                  <div
                    class="flex py-3 px-4 h-14 items-center gap-3 rounded-xl bg-n-solid-2 outline-1 outline outline-n-container"
                  >
                    <Icon
                      icon="i-lucide-plus"
                      class="text-n-slate-10 size-5 flex-shrink-0"
                    />
                    <InlineInput
                      v-model="newKeyword"
                      :placeholder="
                        t(
                          'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.KEYWORD_PLACEHOLDER'
                        )
                      "
                      @keyup.enter="
                        addKeyword(newKeyword);
                        newKeyword = '';
                      "
                    />
                    <Button
                      :label="
                        t(
                          'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.ADD_KEYWORD'
                        )
                      "
                      icon="i-lucide-corner-down-left"
                      ghost
                      xs
                      slate
                      class="!text-sm !text-n-slate-11 flex-shrink-0"
                      @click="
                        addKeyword(newKeyword);
                        newKeyword = '';
                      "
                    />
                  </div>
                </div>

                <!-- 匹配方式 -->
                <div
                  class="p-4 border-t border-n-slate-3 dark:border-n-slate-6"
                >
                  <label class="block text-xs text-n-slate-11 mb-3">
                    {{
                      t(
                        'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.MATCH_MODE_LABEL'
                      )
                    }}
                  </label>
                  <div class="flex gap-4">
                    <label class="flex items-center gap-2 cursor-pointer">
                      <input
                        v-model="localRule.matchMode"
                        type="radio"
                        value="any"
                        class="text-n-slate-12"
                      />
                      <span class="text-sm text-n-slate-12">
                        {{
                          t(
                            'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.MATCH_ANY'
                          )
                        }}
                      </span>
                    </label>
                    <label class="flex items-center gap-2 cursor-pointer">
                      <input
                        v-model="localRule.matchMode"
                        type="radio"
                        value="all"
                        class="text-n-slate-12"
                      />
                      <span class="text-sm text-n-slate-12">
                        {{
                          t(
                            'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.MATCH_ALL'
                          )
                        }}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部按钮 -->
        <div
          class="flex gap-3 px-6 py-4 border-t border-n-slate-6 dark:border-n-slate-700"
        >
          <Button
            :label="t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.CANCEL')"
            variant="faded"
            class="flex-1"
            @click="closeDrawer"
          />
          <Button
            :label="t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.SAVE')"
            :is-loading="isSaving"
            blue
            class="flex-1"
            @click="saveConfig"
          />
        </div>
      </div>
    </transition>
  </Teleport>

  <!-- 知识库选择弹窗 -->
  <SelectKnowledgeBaseDialog
    ref="selectKbDialogRef"
    action-type="crawlWechat"
    @select="handleKbSelect"
  />
</template>

<style scoped>
/* 渐变边框包裹器 */
.gradient-border-wrapper {
  padding: 1.5px;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
</style>
